import React, { useState, useCallback } from 'react';
import { errorToast, successToast } from '../../../utils/helper';
import SingleExerciseForm from './SingleExerciseForm';
import ExerciseSelectorPanel from './ExerciseSelectorPanel';
import { useWorkoutCreation } from '../../../context/WorkoutCreationContext';
import { useHealthLoading } from '../../../context/HealthLoadingContext';
import WorkoutDraggableList from './WorkoutDraggableList';

const StretchesTab = ({ error, isView }) => {
  // Use workout creation context
  const { workoutData, updateWorkoutData } = useWorkoutCreation();
  const { withLoading } = useHealthLoading();
  const [isStrechChanged, setIsStretchChanged] = useState(false);
  const [mode, setMode] = useState('select');

  // Initialize with empty stretch for form
  const [currentStretch, setCurrentStretch] = useState({
    id: Date.now(),
    name: '',
    equipment_id: '',
    muscle_groups: [],
    exercise_duration: '',
    video_url: '',
    image_url: '',
    video: null,
    videoPreview: '',
    videoDurationMin: '00',
    videoDurationSec: '00',
    image: null,
    imagePreview: ''
  });

  const [stretchErrors, setStretchErrors] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingStretchId, setEditingStretchId] = useState(null);

  // Update current stretch when user makes changes
  const handleStretchChange = useCallback((updatedStretch) => {
    setCurrentStretch(updatedStretch);
    setStretchErrors({});
  }, []);

  // Handle cancel edit mode
  const handleCancelEdit = useCallback(() => {
    // Reset to empty form
    const resetStretch = {
      id: Date.now(),
      name: '',
      equipment_id: '',
      muscle_groups: [],
      exercise_duration: '',
      video_url: '',
      image_url: '',
      video: null,
      videoPreview: '',
      videoDurationMin: '00',
      videoDurationSec: '00',
      image: null,
      imagePreview: ''
    };
    setCurrentStretch(resetStretch);
    setIsEditing(false);
    setEditingStretchId(null);
    setStretchErrors({});
  }, []);

  const handleAddExistingStretch = useCallback(
    (selectedStretch) => {
      const existingStretches = workoutData.stretches || [];
      const stretchExists = existingStretches.some((stretch) => {
        const existingId = stretch.exercise_id?._id || stretch.exercise_id || stretch._id;
        return existingId === selectedStretch.exercise_id;
      });

      if (stretchExists) {
        errorToast('This stretch has already been added to the workout');
        return;
      }

      const updatedStretches = [
        ...existingStretches,
        {
          ...selectedStretch,
          sequence: existingStretches.length + 1,
          metadata: selectedStretch.metadata || { sets: [] }
        }
      ];

      updateWorkoutData({ stretches: updatedStretches });
      setIsStretchChanged(true);
      successToast('Stretch added to workout successfully!');
    },
    [updateWorkoutData, workoutData.stretches]
  );

  // Handle save stretch - receives the saved stretch data from SingleExerciseForm
  const handleSaveStretch = useCallback(
    async (savedStretchData) => {
      try {
        // Check if we have the saved stretch ID
        if (!savedStretchData?._id) {
          errorToast('Stretch must be saved before adding to workout');
          return;
        }

        setIsSaving(true);

        // Get existing stretches from context
        const existingStretches = workoutData.stretches || [];

        // Check if we're editing an existing stretch
        if (isEditing && editingStretchId) {
          // Update existing stretch in the list
          const updatedStretches = existingStretches.map((stretch) => {
            const stretchId = stretch.exercise_id?._id || stretch.exercise_id || stretch._id;
            if (stretchId === editingStretchId) {
              return {
                ...stretch,
                exercise_id: savedStretchData._id,
                _id: savedStretchData._id,
                name: savedStretchData.name,
                muscle_groups: savedStretchData.muscle_groups || [],
                equipment_id:
                  savedStretchData.equipment_id?._id || savedStretchData.equipment_id || '',
                exercise_duration: savedStretchData.exercise_duration,
                video_url: savedStretchData.video_url || '',
                image_url: savedStretchData.image_url || ''
              };
            }
            return stretch;
          });

          // Update context
          updateWorkoutData({
            stretches: updatedStretches
          });
          setIsStretchChanged(true);

          // Reset editing state
          setIsEditing(false);
          setEditingStretchId(null);
        } else {
          // Adding new stretch - check if stretch already exists
          const stretchExists = existingStretches.some((s) => {
            const existingId = s.exercise_id?._id || s.exercise_id || s._id;
            return existingId === savedStretchData._id;
          });

          if (stretchExists) {
            errorToast('This stretch has already been added to the workout');
            setIsSaving(false);
            return;
          }

          // Calculate next sequence
          const nextSequence = existingStretches.length + 1;

          // Add new stretch to existing list - flatten the structure like in ExercisesTab
          const updatedStretches = [
            ...existingStretches,
            {
              exercise_id: savedStretchData._id,
              _id: savedStretchData._id,
              name: savedStretchData.name,
              muscle_groups: savedStretchData.muscle_groups || [],
              equipment_id:
                savedStretchData.equipment_id?._id || savedStretchData.equipment_id || '',
              exercise_duration: savedStretchData.exercise_duration,
              video_url: savedStretchData.video_url || '',
              image_url: savedStretchData.image_url || '',
              sequence: nextSequence,
              metadata: { sets: [] }
            }
          ];

          // Update context
          updateWorkoutData({
            stretches: updatedStretches
          });
          setIsStretchChanged(true);

          successToast('Stretch added to workout successfully!');
        }

        // Reset the form for next stretch
        const resetStretch = {
          id: Date.now(),
          name: '',
          equipment_id: '',
          muscle_groups: [],
          exercise_duration: '',
          video_url: '',
          image_url: '',
          video: null,
          videoPreview: '',
          videoDurationMin: '00',
          videoDurationSec: '00',
          image: null,
          imagePreview: ''
        };
        setCurrentStretch(resetStretch);
        setStretchErrors({});
      } catch (error) {
        console.error('Error adding stretch to workout:', error);
        errorToast(error.message || 'Failed to add stretch to workout');
      } finally {
        setIsSaving(false);
      }
    },
    [workoutData, updateWorkoutData, isEditing, editingStretchId]
  );

  // Handle edit stretch - populate form with existing data
  const handleEditStretch = useCallback((stretch) => {
    // Scroll to form
    window.scrollTo({ top: 0, behavior: 'smooth' });

    // Populate form with existing stretch data
    const stretchToEdit = {
      id: stretch._id,
      name: stretch.name || '',
      equipment_id: stretch.equipment_id?._id || stretch.equipment_id || '',
      muscle_groups: stretch.muscle_groups || [],
      exercise_duration: stretch.exercise_duration || '',
      video_url: stretch.video_url || '',
      image_url: stretch.image_url || '',
      video: null,
      videoPreview: stretch.video_url || '',
      videoDurationMin: '00',
      videoDurationSec: '00',
      image: null,
      imagePreview: stretch.image_url || ''
    };

    setCurrentStretch(stretchToEdit);
    setMode('create');
    setIsEditing(true);
    setEditingStretchId(stretch._id);
    setIsStretchChanged(true);
    setStretchErrors({});
  }, []);

  // Handle reorder stretches
  const handleReorderStretches = useCallback(
    (reorderedData) => {
      const existingStretches = workoutData.stretches || [];

      // Update with new sequences
      const reorderedStretches = existingStretches.map((stretch) => {
        const stretchId = stretch.exercise_id?._id || stretch.exercise_id || stretch._id;
        const reorderedItem = reorderedData.find(
          (item) => (item.exercise_id?._id || item.exercise_id) === stretchId
        );
        return {
          ...stretch,
          sequence: reorderedItem?.sequence || stretch.sequence
        };
      });

      // Sort by sequence
      reorderedStretches.sort((a, b) => a.sequence - b.sequence);

      updateWorkoutData({
        stretches: reorderedStretches
      });
      setIsStretchChanged(true);
    },
    [workoutData.stretches, updateWorkoutData]
  );

  // Handle delete stretch
  const handleDeleteStretch = useCallback(
    async (stretchId) => {
      const existingStretches = workoutData.stretches || [];

      const updatedStretches = existingStretches
        .filter((s) => {
          const sId = s.exercise_id?._id || s.exercise_id || s._id;
          return sId !== stretchId;
        })
        .map((stretch, index) => ({
          ...stretch,
          sequence: index + 1
        }));

      updateWorkoutData({
        stretches: updatedStretches
      });
      setIsStretchChanged(true);
    },
    [workoutData.stretches, updateWorkoutData]
  );

  // Handle save and continue
  const handleSaveAndContinue = useCallback(async () => {
    await withLoading(async () => {
      updateWorkoutData({
        currentStep: 3
      });
      setIsStretchChanged(false);
    });
  }, [updateWorkoutData, withLoading]);

  return (
    <div className="space-y-6">
      {/* {!isView && (
        <div className="flex items-center justify-end gap-3">
          <button
            type="button"
            onClick={() => setMode('select')}
            className={mode === 'select' ? 'btn-primary' : 'btn-secondary'}
          >
            Select Existing Exercise
          </button>
          <button
            type="button"
            onClick={() => setMode('create')}
            className={mode === 'create' ? 'btn-primary' : 'btn-secondary'}
          >
            Add New Exercise
          </button>
        </div>
      )} */}
      {workoutData.stretches && workoutData.stretches.length > 0 && (
        <WorkoutDraggableList
          items={workoutData.stretches}
          setItems={(items) => updateWorkoutData({ stretches: items })}
          droppableId="stretches-list"
          heading={`Added Stretches (${workoutData.stretches.length})`}
          onSubmit={handleReorderStretches}
          onDelete={handleDeleteStretch}
          onEdit={handleEditStretch}
          type="stretch"
          hideSubmitButton={true}
        />
      )}

      {mode === 'select' && !isEditing ? (
        <ExerciseSelectorPanel
          type="stretch"
          selectedItems={workoutData.stretches || []}
          onAddItem={handleAddExistingStretch}
          isView={isView}
        />
      ) : (
        <SingleExerciseForm
          exercise={currentStretch}
          onExerciseChange={handleStretchChange}
          onSave={handleSaveStretch}
          onCancel={handleCancelEdit}
          isView={isView}
          showSaveButton={true}
          exerciseErrors={stretchErrors}
          type="stretch"
          isSaving={isSaving}
          isEditing={isEditing}
          exerciseId={editingStretchId}
        />
      )}

      {/* Draggable List of Saved Stretches */}

      {error?.stretches && (
        <span className="text-red-500 text-[12px] mt-2 block">{error.stretches}</span>
      )}

      {/* Save and Continue Button */}
      {!isView && (
        <div className="flex justify-end mt-6">
          <button
            type="button"
            onClick={handleSaveAndContinue}
            className="btn-primary"
          >
            Save and Continue
          </button>
        </div>
      )}
    </div>
  );
};

export default StretchesTab;
